/**
 * Plot Pavings Column Mapping
 * Plot Pavings uses semantic field names directly (no col1, col2 mapping needed)
 * This file provides a reverse mapping for dimension values
 */

import { EVALUATE_CODES, EvaluateCode } from './constants';
import { formatDimensionLabel, normaliseDecimalMmInLabel } from './dimensionLabel';

/**
 * Reconstruct plot pavings column headers from dimension values.
 * Mirrors the logic in PlotPavings.tsx so bill summary shows the same labels
 * even when PlotPavings hasn't mounted in the current session.
 */
export function reconstructPlotPavingsColumnHeaders(
  columnHeaders: Record<string, string>,
  dimensionValues: Record<string, string>
): Record<string, string> {
  // Normalise any persisted decimal-mm patterns up front (e.g. headers saved
  // before the bill-summary normalisation existed: "0.150mm thick MOT Type 1"
  // → "150mm thick MOT Type 1").
  const result: Record<string, string> = {};
  Object.entries(columnHeaders).forEach(([key, value]) => {
    result[key] = normaliseDecimalMmInLabel(value);
  });

  // Sub base fields — reconstruct from numeric dimension value.
  // Decimal entries (e.g. "0.150") are converted to mm for display via
  // `formatDimensionLabel` so the bill-summary header always shows mm.
  if (dimensionValues['pavingSubBase'] && !result['pavingSubBase']) {
    const normalised = formatDimensionLabel(dimensionValues['pavingSubBase']);
    const v = normalised.includes('mm') ? normalised : `${normalised}mm`;
    result['pavingSubBase'] = `${v} thick Stone bed`;
  }
  if (dimensionValues['macadamSubBase'] && !result['macadamSubBase']) {
    const normalised = formatDimensionLabel(dimensionValues['macadamSubBase']);
    const v = normalised.includes('mm') ? normalised : `${normalised}mm`;
    result['macadamSubBase'] = `${v} thick MOT Type 1`;
  }

  // Evaluate code fields — resolve ALP / GWALP codes to labels
  const evaluateCodeFields = [
    'pavingPlainGreyPavings',
    'macadamDbBase',
    'macadamDbBinder',
    'macadamDbSurfaceCourse',
    'macadamHraSurfaceCourse',
    'macadamSmaSurfaceCourse',
  ];
  evaluateCodeFields.forEach((fieldKey) => {
    const value = dimensionValues[fieldKey];
    if (value && !result[fieldKey]) {
      if (value.startsWith('ALP') || value.startsWith('GWALP')) {
        const code = value as EvaluateCode;
        if (EVALUATE_CODES[code]) {
          result[fieldKey] = EVALUATE_CODES[code].label;
        }
      }
    }
  });

  return result;
}

/**
 * Identity mapping: field name to itself
 * Used for mapping dimension values to their field names
 */
export const PLOT_PAVINGS_REVERSE_MAPPING: { [fieldName: string]: string } = {
  // Paving Paths
  'pavingLevelCompact': 'pavingLevelCompact',
  'pavingGeotextileLayer': 'pavingGeotextileLayer',
  'pavingSubBase': 'pavingSubBase',
  'pavingPlainGreyPavings': 'pavingPlainGreyPavings',
  'slab900wSteps': 'slab900wSteps',
  'slab1200wSteps': 'slab1200wSteps',
  'slabGravelMarginPea': 'slabGravelMarginPea',
  'slabGravelMarginCotswold': 'slabGravelMarginCotswold',
  'slabThresholdDrain': 'slabThresholdDrain',
  'slabShedBase': 'slabShedBase',
  'slabCycleHoops': 'slabCycleHoops',
  'slabRotaryDryer': 'slabRotaryDryer',

  // Macadam Paths
  'macadamLevelCompact': 'macadamLevelCompact',
  'macadamGeotextileLayer': 'macadamGeotextileLayer',
  'macadamSubBase': 'macadamSubBase',
  'macadamDbBase': 'macadamDbBase',
  'macadamDbBinder': 'macadamDbBinder',
  'macadamSealingGrit': 'macadamSealingGrit',
  'macadamSweepTack': 'macadamSweepTack',
  'macadamDbSurfaceCourse': 'macadamDbSurfaceCourse',
  'macadamHraSurfaceCourse': 'macadamHraSurfaceCourse',
  'macadamSmaSurfaceCourse': 'macadamSmaSurfaceCourse',
  'macadamEdgings': 'macadamEdgings',
};
